import { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp, MapPin, Copy, ExternalLink, Edit, AlertTriangle, Users, Fuel, Camera, Tent, Navigation, Droplet, ArrowRight, Clock, Trash2, ArrowUp, ArrowDown, CheckCircle, GripVertical } from '@/ui/icons'
import { Badge } from '@/ui/components'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { ConfirmationDialog } from '@/ui/components'
import { WaypointResponse } from '@/shared/api/routes'
import { WaypointTimeInput } from '../forms/WaypointTimeInput'
import { WaypointNotesEditor } from '../forms/WaypointNotesEditor'
import { formatTimeForDisplay, formatTimeRange, calculateDurationMinutes, formatDuration, isArrivalNextDay, formatDurationAirlineStyle } from '../../utils/timeCalculations'
import { formatCoordinatesForCopy, copyCoordinatesToClipboard, getGoogleMapsUrl } from '../../utils/coordinateUtils'
import { WaypointDistance } from '../../utils/itineraryDistanceUtils'
import { TimeValidationWarning } from '../../utils/itineraryTimeValidation'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { getWaypointTypeStyle, getWaypointTypeLabel, getWaypointTypeIconWithCustom, getCustomWaypointStyle, isRouteStop, type POIType } from '@/pages/itinerary-builder/utils/waypointTypeStyles'
import { useConfirm } from '@/shared/hooks/useConfirm'
import { WaypointTypeDropdown } from '../waypoints/WaypointTypeDropdown'

interface ItineraryWaypointCardProps {
  waypoint: WaypointResponse
  previousWaypoint: WaypointResponse | null
  distance: WaypointDistance | null
  timeWarning: TimeValidationWarning | null
  index: number
  isLast: boolean
  tripId: string
  isOwner?: boolean
  onUpdateWaypoint: (waypointId: string, updates: { start_time?: string | null; end_time?: string | null; notes?: string | null; name?: string; poi_type?: POIType; poi_custom_label?: string | null }) => Promise<void>
  onDeleteWaypoint?: (waypointId: string) => Promise<void>
  hasDetours?: boolean // Whether this route stop has detours nested under it
  isDetour?: boolean // Whether this waypoint is a detour (nested under a route stop)
  /** @deprecated Use drag-and-drop instead */
  onMoveUp?: (waypointId: string) => void
  /** @deprecated Use drag-and-drop instead */
  onMoveDown?: (waypointId: string) => void
  /** @deprecated Use drag-and-drop instead */
  canMoveUp?: boolean
  /** @deprecated Use drag-and-drop instead */
  canMoveDown?: boolean
  isDraggable?: boolean
  dragAttributes?: any
  dragListeners?: any
}

/**
 * Get POI type icon component
 */
function getPOIIcon(poiType: string | undefined | null) {
  switch (poiType) {
    case 'meetup_spot':
      return <Users className="w-4 h-4" />
    case 'gas':
    case 'fuel':
      return <Fuel className="w-4 h-4" />
    case 'viewpoint':
    case 'scenic':
      return <Camera className="w-4 h-4" />
    case 'campsite':
    case 'campground':
      return <Tent className="w-4 h-4" />
    case 'trailhead':
      return <Navigation className="w-4 h-4" />
    case 'water':
    case 'water_source':
      return <Droplet className="w-4 h-4" />
    default:
      return <MapPin className="w-4 h-4" />
  }
}

/**
 * Get POI type badge color
 */
function getPOIBadgeVariant(poiType: string | undefined | null): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (poiType) {
    case 'meetup_spot':
      return 'default'
    case 'gas':
    case 'fuel':
      return 'destructive'
    case 'viewpoint':
    case 'scenic':
      return 'secondary'
    case 'campsite':
    case 'trailhead':
      return 'default'
    default:
      return 'outline'
  }
}

/**
 * Format POI type for display
 */
function formatPoiType(poiType: string | undefined | null, customLabel?: string): string | null {
  if (!poiType) return null
  if (poiType === 'custom') return customLabel || null
  
  const labels: Record<string, string> = {
    gas: 'Fuel',
    water: 'Water',
    viewpoint: 'scenic',
    campsite: 'campsite',
    meetup_spot: 'meetup',
    trailhead: 'trailhead'
  }
  
  return labels[poiType] || poiType.replace('_', ' ')
}

/**
 * Get display name for waypoint
 */
function getDisplayName(waypoint: WaypointResponse, index: number): string {
  // Use name if it's not a default generated name
  if (waypoint.name && !waypoint.name.match(/^Waypoint\s+\d{1,2}-\d{1,2}-\d{4}/i)) {
    return waypoint.name
  }
  
  // Use POI address if available
  if (waypoint.poi_address) {
    return waypoint.poi_address
  }
  
  // Use POI type to generate a friendly name
  if (waypoint.poi_type) {
    if (waypoint.poi_type === 'custom' && waypoint.poi_custom_label) {
      return waypoint.poi_custom_label
    }
    if (waypoint.poi_type !== 'custom') {
      const poiTypeLabels: Record<string, string> = {
        gas: 'Fuel',
        water: 'Water',
        viewpoint: 'Viewpoint',
        campsite: 'Campsite',
        meetup_spot: 'Meetup Spot',
        trailhead: 'Trailhead'
      }
      const label = poiTypeLabels[waypoint.poi_type] || waypoint.poi_type
      return `${label} ${index + 1}`
    }
  }
  
  // Fallback to generic "Stop"
  return `Stop ${index + 1}`
}

export function ItineraryWaypointCard({
  waypoint,
  previousWaypoint,
  distance,
  timeWarning,
  index,
  isLast,
  tripId,
  isOwner = false,
  onUpdateWaypoint,
  onDeleteWaypoint,
  hasDetours = false,
  isDetour = false,
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
  isDraggable = false,
  dragAttributes,
  dragListeners
}: ItineraryWaypointCardProps) {
  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()
  const [isExpanded, setIsExpanded] = useState(false)
  const [editingTimeField, setEditingTimeField] = useState<'start_time' | 'end_time' | null>(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [localName, setLocalName] = useState(waypoint.name || '')
  const [isUpdatingName, setIsUpdatingName] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [copiedCoordinates, setCopiedCoordinates] = useState(false)
  const pendingNameUpdateRef = useRef<{ value: string } | null>(null)
  // Local optimistic state for times - allows immediate UI updates without waiting for API
  const [optimisticStartTime, setOptimisticStartTime] = useState<string | null | undefined>(waypoint.start_time)
  const [optimisticEndTime, setOptimisticEndTime] = useState<string | null | undefined>(waypoint.end_time)
  // Local optimistic state for notes - allows immediate UI updates without waiting for API
  const [optimisticNotes, setOptimisticNotes] = useState<string | null | undefined>(waypoint.notes)
  
  // Track the last time we updated to prevent resetting optimistic state
  const lastUpdateTimeRef = useRef<{ field: 'start_time' | 'end_time' | 'notes' | null; timestamp: number }>({ field: null, timestamp: 0 })

  /**
   * Normalize time format for comparison (HH:MM:SS -> HH:MM)
   * Handles format mismatches between '10:10' and '10:10:00'
   */
  const normalizeTimeForComparison = (time: string | null | undefined): string | null => {
    if (!time) return null
    // If it's HH:MM:SS format, convert to HH:MM
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time.substring(0, 5)
    }
    return time
  }

  // Sync optimistic state with prop changes (when data comes back from server)
  // Only sync if we're not currently editing that field AND not in the middle of an update
  useEffect(() => {
    const now = Date.now()
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current.timestamp
    const isRecentUpdate = timeSinceLastUpdate < 10000 // 10 second window to allow for API call + route refresh + database propagation
    
    // Don't sync if we recently updated this field (prevents resetting optimistic state during update)
    if (isRecentUpdate && lastUpdateTimeRef.current.field) {
      const fieldBeingUpdated = lastUpdateTimeRef.current.field
      const isEditingThatField = editingTimeField === fieldBeingUpdated
      
      // If we're editing the field we just updated, don't sync (user is still editing)
      if (isEditingThatField) {
        return
      }
      
      // If we updated this field recently, check if the server value matches our optimistic value
      // If it does, we can sync. If it doesn't, the server might have stale data, so keep optimistic value
      const waypointStartTime = normalizeTimeForComparison(waypoint.start_time)
      const waypointEndTime = normalizeTimeForComparison(waypoint.end_time)
      const optimisticStart = normalizeTimeForComparison(optimisticStartTime)
      const optimisticEnd = normalizeTimeForComparison(optimisticEndTime)
      
      if (fieldBeingUpdated === 'start_time' && waypointStartTime !== optimisticStart) {
        // Server value doesn't match our optimistic value - might be stale data from route refresh
        // Keep optimistic value for now
        return
      }
      
      if (fieldBeingUpdated === 'end_time' && waypointEndTime !== optimisticEnd) {
        // Server value doesn't match our optimistic value - might be stale data from route refresh
        // Keep optimistic value for now
        return
      }
      
      // If server value matches optimistic, allow sync after 3 seconds (gives time for route refresh to complete)
      if (timeSinceLastUpdate < 3000) {
        return
      }
    }
    
    // Sync start_time if not currently editing it
    if (editingTimeField !== 'start_time') {
      const waypointStartTime = normalizeTimeForComparison(waypoint.start_time)
      const currentOptimistic = normalizeTimeForComparison(optimisticStartTime)
      
      // Only sync if the normalized values are actually different
      if (waypointStartTime !== currentOptimistic) {
        setOptimisticStartTime(waypoint.start_time)
      }
    }
    
    // Sync end_time if not currently editing it
    if (editingTimeField !== 'end_time') {
      const waypointEndTime = normalizeTimeForComparison(waypoint.end_time)
      const currentOptimistic = normalizeTimeForComparison(optimisticEndTime)
      
      // Only sync if the normalized values are actually different
      if (waypointEndTime !== currentOptimistic) {
        setOptimisticEndTime(waypoint.end_time)
      }
    }
    
    if (!isEditingName) {
      setLocalName(waypoint.name || '')
    }
    
    // Sync notes if not currently editing
    if (!isEditingNotes) {
      const waypointNotes = waypoint.notes ?? null
      const currentOptimistic = optimisticNotes ?? null
      // Only sync if values are actually different
      if (waypointNotes !== currentOptimistic) {
        setOptimisticNotes(waypoint.notes)
      }
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // We intentionally don't include optimisticStartTime/optimisticEndTime/optimisticNotes in deps
    // to avoid loops - we only want to react to waypoint prop changes
  }, [waypoint.id, waypoint.start_time, waypoint.end_time, waypoint.name, waypoint.notes, editingTimeField, isEditingName, isEditingNotes])

  const coordinates = formatCoordinatesForCopy(waypoint.latitude, waypoint.longitude)
  const displayName = getDisplayName(waypoint, index)
  const poiTypeLabel = formatPoiType(waypoint.poi_type, waypoint.poi_custom_label)
  const poiIcon = getPOIIcon(waypoint.poi_type)
  const poiBadgeVariant = getPOIBadgeVariant(waypoint.poi_type)
  
  // Get waypoint type style from design system
  // Check for custom styling first (Overlook, Lunch Stop, etc.)
  const customStyle = getCustomWaypointStyle(waypoint.poi_custom_label)
  const baseTypeStyle = getWaypointTypeStyle(waypoint.poi_type ?? null)
  const typeStyle = customStyle || baseTypeStyle
  const TypeIcon = getWaypointTypeIconWithCustom(waypoint.poi_type ?? null, waypoint.poi_custom_label)
  const typeLabel = getWaypointTypeLabel(waypoint.poi_type ?? null, waypoint.poi_custom_label)

  // Use optimistic times for display, fallback to waypoint times
  const displayStartTime = optimisticStartTime ?? waypoint.start_time
  const displayEndTime = optimisticEndTime ?? waypoint.end_time

  // Format times - use 12-hour format for display
  const startTime = displayStartTime ? formatTimeForDisplay(displayStartTime) : null
  const endTime = displayEndTime ? formatTimeForDisplay(displayEndTime) : null
  const timeRange = formatTimeRange(displayStartTime, displayEndTime)
  const durationMinutes = calculateDurationMinutes(displayStartTime, displayEndTime)
  const durationDisplay = durationMinutes !== null ? formatDuration(durationMinutes) : null
  const durationDisplayAirline = durationMinutes !== null ? formatDurationAirlineStyle(durationMinutes) : null
  const isNextDay = isArrivalNextDay(displayStartTime, displayEndTime)

  const handleTimeUpdate = async (field: 'start_time' | 'end_time', value: string | null, shouldCloseField = false) => {
    // Record that we're updating this field to prevent useEffect from resetting
    lastUpdateTimeRef.current = { field, timestamp: Date.now() }
    
    // Optimistic update - update UI immediately without waiting for API
    if (field === 'start_time') {
      setOptimisticStartTime(value)
    } else {
      setOptimisticEndTime(value)
    }
    
    // Update in background - don't await, let it happen asynchronously
    onUpdateWaypoint(waypoint.id, { [field]: value })
      .then(() => {
        // Close editing field after successful update (if requested)
        if (shouldCloseField) {
          // Use a small delay to ensure the popover has time to close properly
          setTimeout(() => {
            setEditingTimeField(null)
          }, 100)
        }
        // Clear the update tracking after a delay to allow route schedule refresh to complete
        // This ensures the useEffect can sync the optimistic state with the server response
        setTimeout(() => {
          // Only clear if this is still the most recent update
          if (lastUpdateTimeRef.current.field === field) {
            lastUpdateTimeRef.current = { field: null, timestamp: 0 }
          }
        }, 3000) // 3 second delay to allow route schedule refresh
      })
      .catch((error) => {
        // Revert optimistic update on error
        if (field === 'start_time') {
          setOptimisticStartTime(waypoint.start_time)
        } else {
          setOptimisticEndTime(waypoint.end_time)
        }
        // Clear the update tracking on error so we can sync again
        lastUpdateTimeRef.current = { field: null, timestamp: 0 }
      })
  }

  const handleNotesUpdate = async (notes: string | null) => {
    // Optimistic update - update UI immediately
    setOptimisticNotes(notes)
    lastUpdateTimeRef.current = { field: 'notes', timestamp: Date.now() }
    
    try {
      await onUpdateWaypoint(waypoint.id, { notes })
      // Clear the update tracking after a delay to allow refresh to complete
      setTimeout(() => {
        if (lastUpdateTimeRef.current.field === 'notes') {
          lastUpdateTimeRef.current = { field: null, timestamp: 0 }
        }
      }, 2000)
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticNotes(waypoint.notes)
      lastUpdateTimeRef.current = { field: null, timestamp: 0 }
      throw error
    }
  }

  // Debounced update function for name - only updates after user stops typing for 1500ms
  const debouncedUpdateName = useDebouncedCallback(async (value: string) => {
    const trimmedValue = value.trim()
    
    // Don't update if value hasn't changed
    if (trimmedValue === waypoint.name) {
      pendingNameUpdateRef.current = null
      return
    }

    try {
      setIsUpdatingName(true)
      await onUpdateWaypoint(waypoint.id, { name: trimmedValue })
      pendingNameUpdateRef.current = null
    } catch (error) {
      // Revert to server value on error
      setLocalName(waypoint.name || '')
      pendingNameUpdateRef.current = null
    } finally {
      setIsUpdatingName(false)
    }
  }, 1500)

  // Immediate update function for blur events
  const updateNameImmediately = async (value: string) => {
    // Cancel any pending debounced update
    pendingNameUpdateRef.current = null

    const trimmedValue = value.trim()
    
    // Don't update if value hasn't changed
    if (trimmedValue === waypoint.name) {
      setIsEditingName(false)
      return
    }

    try {
      setIsUpdatingName(true)
      await onUpdateWaypoint(waypoint.id, { name: trimmedValue })
      setIsEditingName(false)
    } catch (error) {
      // Revert to server value on error
      setLocalName(waypoint.name || '')
    } finally {
      setIsUpdatingName(false)
    }
  }

  const handleNameChange = (value: string) => {
    // Update local state immediately for instant UI feedback
    setLocalName(value)
    
    // Store pending update
    pendingNameUpdateRef.current = { value }

    // Debounce the API call
    debouncedUpdateName(value)
  }

  const handleNameBlur = () => {
    // If there's a pending update, execute it immediately
    if (pendingNameUpdateRef.current) {
      updateNameImmediately(pendingNameUpdateRef.current.value)
    } else {
      setIsEditingName(false)
    }
  }

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Stop propagation to prevent parent handlers from interfering
    e.stopPropagation()
    
    if (e.key === 'Enter') {
      e.preventDefault()
      if (pendingNameUpdateRef.current) {
        updateNameImmediately(pendingNameUpdateRef.current.value)
      } else {
        setIsEditingName(false)
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setLocalName(waypoint.name || '')
      pendingNameUpdateRef.current = null
      setIsEditingName(false)
    }
    // Allow space and other keys to work normally in the input
  }

  const handleMoveUp = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onMoveUp && canMoveUp && !isMoving) {
      setIsMoving(true)
      onMoveUp(waypoint.id)
      // Reset after a short delay to allow for animation
      setTimeout(() => setIsMoving(false), 300)
    }
  }

  const handleMoveDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onMoveDown && canMoveDown && !isMoving) {
      setIsMoving(true)
      onMoveDown(waypoint.id)
      // Reset after a short delay to allow for animation
      setTimeout(() => setIsMoving(false), 300)
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!onDeleteWaypoint) return
    
    const displayName = getDisplayName(waypoint, index)
    
    await confirm(
      {
        title: 'Delete Waypoint',
        description: `Are you sure you want to delete "${displayName}"? This action cannot be undone.`,
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setIsDeleting(true)
        setConfirmLoading(true)
        try {
          await onDeleteWaypoint(waypoint.id)
        } catch (error) {
          throw error
        } finally {
          setIsDeleting(false)
          setConfirmLoading(false)
        }
      }
    )
  }

  const handleCopyCoordinates = async () => {
    const success = await copyCoordinatesToClipboard(waypoint.latitude, waypoint.longitude)
    if (success) {
      setCopiedCoordinates(true)
      setTimeout(() => setCopiedCoordinates(false), 2000)
    }
  }

  const googleMapsUrl = getGoogleMapsUrl(waypoint.latitude, waypoint.longitude)

  // Determine if this is a route stop (null poi_type, campsite, or meetup_spot) or detour
  const waypointIsRouteStop = isRouteStop(waypoint.poi_type)

  // Collapsed view
  if (!isExpanded) {
    return (
      <div className={`relative mb-3 flex items-start gap-3 ${isDetour ? 'ml-0' : ''}`}>
        {/* Timeline visualization */}
        <div className="flex flex-col items-center flex-shrink-0 pt-2">
          {/* Timeline line - extend if has detours or not last */}
          {(!isLast || hasDetours) && (
            <div className={`w-0.5 h-full min-h-[40px] ${
              isDetour ? 'bg-earth-200' : 'bg-blue-200'
            }`} />
          )}
          {/* Timeline circle */}
          <div className={`relative z-10 ${
            waypointIsRouteStop 
              ? 'w-3 h-3 rounded-full bg-blue-500 border-2 border-white' 
              : isDetour
              ? 'w-2.5 h-2.5 rounded-full border-2 border-earth-400 bg-white'
              : 'w-3 h-3 rounded-full border-2 border-blue-500 bg-white'
          }`} />
        </div>

        {/* Color-coded waypoint card */}
        <div 
          className={`relative flex items-start gap-3 rounded-lg border transition-colors cursor-pointer bg-white flex-1 ${
            `${typeStyle.borderColor} border`
          }`}
          onClick={() => setIsExpanded(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsExpanded(true)
            }
          }}
          aria-label="Expand waypoint details"
        >
          {/* Drag handle (only shown when draggable) */}
          {isDraggable && dragAttributes && dragListeners && (
            <div
              {...dragAttributes}
              {...dragListeners}
              className="flex-shrink-0 flex items-start justify-center w-8 pt-3 cursor-grab active:cursor-grabbing text-earth-400 hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Drag to reorder"
            >
              <GripVertical className="w-4 h-4" />
            </div>
          )}
          
          {/* Left edge colored indicator */}
          <div className={`w-1 ${typeStyle.indicatorColor} rounded-l-lg flex-shrink-0`} />
          
          {/* Card content */}
          <div className="flex-1 min-w-0 p-3">
            <div className="flex items-start gap-3">
              {/* Main content */}
              <div className="flex-1 min-w-0">
                {/* 1. Waypoint title (most important) */}
                <div className="flex items-center gap-2 mb-1.5 min-w-0">
                  <h4 className="text-heading-sm text-earth-900 truncate flex-1 min-w-0" title={displayName}>
                    {displayName}
                  </h4>
                  <span className={`text-caption px-2 py-0.5 rounded ${typeStyle.bgColor} ${typeStyle.textColor} flex-shrink-0 whitespace-nowrap`}>
                    {typeLabel}
                  </span>
                </div>
                
                {/* 2. Coordinates */}
                {(coordinates || waypoint.poi_address) && (
                  <div className="mb-1.5">
                    {waypoint.poi_address ? (
                      <p className="text-body-sm text-earth-600 dark:text-foreground">{waypoint.poi_address}</p>
                    ) : coordinates ? (
                      <p className="text-body-sm text-earth-600 dark:text-foreground">{coordinates}</p>
                    ) : null}
                  </div>
                )}
                
                {/* 3. Notes/Description */}
                {waypoint.notes && (
                  <p className="text-body-sm text-earth-500 mb-1.5 line-clamp-2">
                    {waypoint.notes}
                  </p>
                )}
                
                {/* 4. Time display (last) - Arrive / Leave so user sees when to be there without opening */}
                {(startTime || endTime) && (
                  <div className="flex items-center gap-1.5 text-body-sm text-earth-600 dark:text-foreground">
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                    {startTime && endTime ? (
                      <span>
                        Arrive {startTime} · Leave {endTime}
                        {durationDisplay != null && ` · ${durationDisplay}`}
                      </span>
                    ) : startTime ? (
                      <span>Arrive {startTime}</span>
                    ) : (
                      <span>Leave by {endTime}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Expand indicator */}
              <div className="flex-shrink-0 text-earth-400 transition-colors">
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Expanded view
  return (
    <div className={`relative mb-3 flex items-start gap-3 ${isDetour ? 'ml-0' : ''}`}>
      {/* Timeline visualization */}
      <div className="flex flex-col items-center flex-shrink-0 pt-2">
        {/* Timeline line - extend if has detours or not last */}
        {(!isLast || hasDetours) && (
          <div className={`w-0.5 h-full min-h-[200px] ${
            isDetour ? 'bg-earth-200' : 'bg-blue-200'
          }`} />
        )}
        {/* Timeline circle */}
        <div className={`relative z-10 ${
          waypointIsRouteStop 
            ? 'w-3 h-3 rounded-full bg-blue-500 border-2 border-white' 
            : isDetour
            ? 'w-2.5 h-2.5 rounded-full border-2 border-earth-400 bg-white'
            : 'w-3 h-3 rounded-full border-2 border-blue-500 bg-white'
        }`} />
      </div>

      {/* Color-coded waypoint card */}
      <div 
        className={`relative flex items-start gap-3 rounded-lg border transition-colors bg-white flex-1 ${
          `${typeStyle.borderColor} border`
        }`}
        onClick={() => {
          // Don't collapse if editing name or time fields
          if (!isEditingName && !editingTimeField) {
            setIsExpanded(false)
          }
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          // Don't handle keyboard events if editing
          if (isEditingName || editingTimeField || isEditingNotes) {
            return
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsExpanded(false)
          }
        }}
        aria-label="Collapse waypoint details"
      >
        {/* Drag handle (only shown when draggable) */}
        {isDraggable && dragAttributes && dragListeners && (
          <div
            {...dragAttributes}
            {...dragListeners}
            className="flex-shrink-0 flex items-start justify-center w-8 pt-3 cursor-grab active:cursor-grabbing text-earth-400 hover:text-primary transition-colors"
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        
        {/* Left edge colored indicator */}
        <div className={`w-1 ${typeStyle.indicatorColor} rounded-l-lg flex-shrink-0`} />
        
        {/* Card content */}
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-start gap-3">
            {/* Main content */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {isEditingName ? (
                      <div className="flex-1 min-w-0">
                        <Input
                          value={localName}
                          onChange={(e) => handleNameChange(e.target.value)}
                          onBlur={handleNameBlur}
                          onKeyDown={handleNameKeyDown}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Enter waypoint name"
                          maxLength={255}
                          disabled={isUpdatingName || !isOwner}
                          className="text-body-sm font-medium"
                          autoFocus
                        />
                        <p className="text-caption text-earth-500 mt-1">Press Enter to save, Esc to cancel</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <h4 
                          className={`text-heading-sm text-earth-900 truncate flex-1 min-w-0 ${isOwner ? 'cursor-pointer hover:text-primary transition-colors' : 'cursor-default'}`}
                          onClick={(e) => {
                            if (!isOwner) return
                            e.stopPropagation()
                            setIsEditingName(true)
                          }}
                          title={displayName || (isOwner ? "Click to edit name" : "Only trip owners can edit waypoints")}
                        >
                          {displayName}
                        </h4>
                        {isOwner ? (
                          <WaypointTypeDropdown
                            onSelect={async (poiType, customLabel) => {
                              const updates: { poi_type?: POIType; poi_custom_label?: string | null } = {}
                              if (poiType !== waypoint.poi_type || customLabel !== waypoint.poi_custom_label) {
                                updates.poi_type = poiType
                                updates.poi_custom_label = customLabel || null
                                await onUpdateWaypoint(waypoint.id, updates)
                              }
                            }}
                            trigger={
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className={`text-caption px-2 py-0.5 rounded ${typeStyle.bgColor} ${typeStyle.textColor} hover:opacity-80 transition-opacity cursor-pointer flex items-center gap-1 flex-shrink-0 whitespace-nowrap`}
                                title="Click to change waypoint type"
                              >
                                {typeLabel}
                                <Edit className="w-3 h-3" />
                              </button>
                            }
                            align="start"
                          />
                        ) : (
                          <span className={`text-caption px-2 py-0.5 rounded ${typeStyle.bgColor} ${typeStyle.textColor} flex-shrink-0 whitespace-nowrap`}>
                            {typeLabel}
                          </span>
                        )}
                      </div>
                    )}
                    {timeWarning?.hasWarning && (
                      <Badge variant="destructive" className="text-xs flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Time warning
                      </Badge>
                    )}
                  </div>

                  {/* Coordinates - Clickable to copy */}
                  {coordinates && (
                    <div className="flex items-center gap-2 mb-2 text-earth-900 dark:text-foreground">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCopyCoordinates()
                        }}
                        className="text-body-sm text-earth-600 hover:text-primary transition-colors flex items-center gap-1.5 group"
                        title="Click to copy coordinates"
                      >
                        <span>{coordinates}</span>
                        {copiedCoordinates ? (
                          <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        )}
                      </button>
                      {googleMapsUrl && (
                        <a
                          href={googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-body-sm text-primary hover:text-primary/80 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Address */}
                  {waypoint.poi_address && (
                    <div className="text-body-sm text-earth-600 dark:text-foreground mb-2">{waypoint.poi_address}</div>
                  )}
                </div>

                {/* Collapse indicator */}
                <div className="flex-shrink-0 text-earth-400 transition-colors">
                  <ChevronUp className="w-5 h-5" />
                </div>
              </div>

              {/* Time section - Airline-style layout */}
              <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="bg-adventure-50/50 border border-adventure-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                {/* Arrival Time (Left) */}
                <div className="flex-1 min-w-[120px]">
                  <div className="text-caption text-earth-600 mb-1.5">Arrival</div>
                  {editingTimeField === 'start_time' ? (
                    <div className="space-y-1">
                      <WaypointTimeInput
                        value={optimisticStartTime || null}
                        onChange={(value) => {
                          handleTimeUpdate('start_time', value, true)
                        }}
                        onBlur={() => {
                          // Don't close immediately on blur - let the onChange handler close it after update
                          // This prevents the field from closing before the value is saved
                        }}
                        onCancel={() => {
                          // Revert optimistic update on cancel
                          setOptimisticStartTime(waypoint.start_time)
                          setEditingTimeField(null)
                          lastUpdateTimeRef.current = { field: null, timestamp: 0 }
                        }}
                        className="w-full"
                      />
                      <p className="text-caption text-earth-500">Select time to save</p>
                    </div>
                  ) : (
                    <div
                      className={isOwner ? "cursor-pointer hover:text-primary transition-colors" : "cursor-default opacity-60"}
                      onClick={(e) => {
                        if (!isOwner) return
                        e.stopPropagation()
                        setEditingTimeField('start_time')
                      }}
                      title={isOwner ? "Click to edit arrival time" : "Only trip owners can edit times"}
                    >
                      {startTime ? (
                        <div className="text-2xl font-semibold text-earth-900">{startTime}</div>
                      ) : (
                        <div className="text-body-lg text-earth-500 italic">Not set</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Center: Duration and Arrow */}
                {startTime && endTime && (
                  <div className="flex flex-col items-center gap-2 px-4">
                    {durationDisplayAirline && (
                      <div className="text-body-sm text-label text-earth-900 text-center whitespace-nowrap">
                        {durationDisplayAirline}
                      </div>
                    )}
                    <ArrowRight className="w-5 h-5 text-primary flex-shrink-0" />
                    {isNextDay && (
                      <div className="text-caption text-earth-600 bg-adventure-100 px-2 py-0.5 rounded whitespace-nowrap">
                        +1 day
                      </div>
                    )}
                  </div>
                )}

                {/* Departure Time (Right) */}
                <div className="flex-1 min-w-[120px] text-right">
                  <div className="text-caption text-earth-600 mb-1.5">Departure</div>
                  {editingTimeField === 'end_time' ? (
                    <div className="space-y-1">
                      <WaypointTimeInput
                        value={optimisticEndTime || null}
                        onChange={(value) => handleTimeUpdate('end_time', value, true)}
                        onBlur={() => {
                          // Don't close immediately on blur - let the onChange handler close it after update
                          // This prevents the field from closing before the value is saved
                        }}
                        onCancel={() => {
                          // Revert optimistic update on cancel
                          setOptimisticEndTime(waypoint.end_time)
                          setEditingTimeField(null)
                          lastUpdateTimeRef.current = { field: null, timestamp: 0 }
                        }}
                        className="w-full"
                      />
                      <p className="text-caption text-earth-500">Select time to save</p>
                    </div>
                  ) : (
                    <div
                      className={isOwner ? "cursor-pointer hover:text-primary transition-colors inline-block" : "cursor-default opacity-60 inline-block"}
                      onClick={(e) => {
                        if (!isOwner) return
                        e.stopPropagation()
                        setEditingTimeField('end_time')
                      }}
                      title={isOwner ? "Click to edit departure time" : "Only trip owners can edit times"}
                    >
                      {endTime ? (
                        <div className="text-2xl font-semibold text-earth-900">{endTime}</div>
                      ) : (
                        <div className="text-body-lg text-earth-500 italic">Not set</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

              {/* Distance from previous */}
              {distance && (
                <div className="text-body-sm">
                  <span className="text-earth-600">Distance from previous: </span>
                  <span className="text-label text-earth-900">{distance.distanceFormatted}</span>
                </div>
              )}

              {/* Time warning */}
              {timeWarning?.hasWarning && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-body-sm text-yellow-800">
                      {timeWarning.message}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <div className="text-label text-earth-900">Notes</div>
                <WaypointNotesEditor
                  value={optimisticNotes ?? waypoint.notes}
                  onChange={handleNotesUpdate}
                  isEditing={isEditingNotes}
                  onEditingChange={setIsEditingNotes}
                  disabled={!isOwner}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-earth-200" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsExpanded(false)
                    }}
                    className="flex items-center gap-1.5"
                  >
                    <ChevronUp className="w-4 h-4" />
                    Collapse
                  </Button>
                  {/* Move up/down buttons (deprecated - only show if not using drag-and-drop) */}
                  {!isDraggable && isOwner && (onMoveUp || onMoveDown) && (
                    <div className="flex flex-col">
                      <button
                        onClick={handleMoveUp}
                        disabled={!canMoveUp || isMoving}
                        className="flex-shrink-0 text-earth-400 hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                        aria-label="Move up"
                        title="Move up"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleMoveDown}
                        disabled={!canMoveDown || isMoving}
                        className="flex-shrink-0 text-earth-400 hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                        aria-label="Move down"
                        title="Move down"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {googleMapsUrl && (
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body-sm text-primary hover:text-primary/80 flex items-center gap-1"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View on map
                    </a>
                  )}
                  {onDeleteWaypoint && isOwner && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex items-center gap-1.5"
                    >
                      <Trash2 className="w-4 h-4" />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </div>
  )
}

